<template>
    <v-autocomplete
        v-bind="attrs"
        v-model:search="search"
        :model-value="model"
        multiple
        chips
        closable-chips
        @update:model-value="onUpdateModelValue"
    >
        <template v-for="name in forwardedSlotNames" :key="name" #[name]="slotData">
            <slot :name="name" v-bind="slotData || {}" />
        </template>

        <!-- Przy `chips` Vuetify renderuje slot `chip`, a nie `selection` -->
        <template v-if="$slots.selection" #chip="slotData">
            <slot name="selection" v-bind="slotData" />
        </template>

        <template v-if="!$slots.selection" #selection="{ item }">
            <v-chip size="small" closable @click:close="removeChip(item)">
                {{ getChipTitle(item) }}
            </v-chip>
        </template>
    </v-autocomplete>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, useAttrs, useSlots } from 'vue'

defineOptions({
    inheritAttrs: false
})

interface AutocompleteItem {
    title?: string
    value?: unknown
    raw?: Record<string, unknown>
}

// 2. Props / Emits
const model = defineModel<unknown>()
const search = defineModel<string>('search', { default: '' })

// 5. Reactive state (refs/reactive)
const attrs = useAttrs()
const slots = useSlots()

// 7. Computed
const forwardedSlotNames = computed(() => Object.keys(slots).filter((name) => name !== 'selection'))

// 8. Handlers / methods
function isReturnObjectMode(): boolean {
    const value = attrs.returnObject ?? attrs['return-object']

    return value !== undefined && value !== false
}

function onUpdateModelValue(value: unknown): void {
    model.value = value
    search.value = ''
}

function getChipTitle(item: AutocompleteItem): string {
    if (item.title) {
        return String(item.title)
    }

    const raw = item.raw ?? {}

    return String(raw.name ?? raw.fullName ?? raw.number ?? item.value ?? '')
}

function getChipId(item: AutocompleteItem): unknown {
    return item.value ?? item.raw?.id
}

function removeChip(item: AutocompleteItem): void {
    const id = getChipId(item)
    const current = model.value

    if (!Array.isArray(current)) {
        return
    }

    if (isReturnObjectMode()) {
        model.value = current.filter((entry) => {
            if (entry && typeof entry === 'object' && 'id' in entry) {
                return (entry as { id: unknown }).id !== id
            }

            return entry !== id
        })
        return
    }

    model.value = current.filter((entry) => entry !== id)
}
</script>
